import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Menu } from 'antd';
import styles from './date-picker-user-management.module.scss';
import { cloneDeep } from 'lodash';
import { arrow_calendar, arrow_next, arrow_prev, calendar } from '../../assets/img';
import { BasicButton, BasicSelect } from '../../assets/common';

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
const currentQuarter = Math.floor((currentMonth - 1) / 3) + 1;
const yearViewInitial = Math.floor(currentYear / 12) * 12;

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const monthsInQuarter = {
  Q1: ['Jan', 'Feb', 'Mar'],
  Q2: ['Apr', 'May', 'Jun'],
  Q3: ['Jul', 'Aug', 'Sep'],
  Q4: ['Oct', 'Nov', 'Dec'],
};

const getQuarterMonths = (quarter) => {
  switch (quarter) {
    case 1:
      return ['Jan', 'Feb', 'Mar'];
    case 2:
      return ['Apr', 'May', 'Jun'];
    case 3:
      return ['Jul', 'Aug', 'Sep'];
    case 4:
      return ['Oct', 'Nov', 'Dec'];
    default:
      return [];
  }
};

const monthOptionInitial = {
  selectedHeader: currentYear,
  selectedValue: [monthNames[currentMonth - 1]],
};

const quarterOptionInitial = {
  selectedHeader: currentYear,
  selectedValue: getQuarterMonths(currentQuarter),
};

const yearOptionInitial = {
  selectedHeader: yearViewInitial,
  selectedValue: [currentYear],
};

const appliedDataInitial = {
  mode: 'month',
  monthOption: {
    selectedHeader: currentYear,
    selectedValue: [],
  },
  quarterOption: {
    selectedHeader: currentYear,
    selectedValue: [],
  },
  yearOption: {
    selectedHeader: yearViewInitial,
    selectedValue: [],
  },
};

const DatePickerUserManagement = ({ onApply, placeholder = 'Select Date', appliedDataDefault = appliedDataInitial, isDisableApply = false }) => {
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState(appliedDataInitial.mode);
  const appliedData = useRef(appliedDataDefault);

  const [monthOption, setMonthOption] = useState(monthOptionInitial);
  const [quarterOption, setQuarterOption] = useState(quarterOptionInitial);
  const [yearOption, setyearOption] = useState(yearOptionInitial);

  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const checkNotApply = () =>
    appliedData.current.monthOption.selectedValue.length < 1 &&
    appliedData.current.yearOption.selectedValue.length < 1 &&
    appliedData.current.quarterOption.selectedValue.length < 1;

  const handleRenderText = () => {
    const isNotApply = checkNotApply();
    if (isNotApply) return '';

    if (appliedData.current.mode === 'quarter') {
      const startMonth = new Date(
        `${appliedData.current.quarterOption.selectedValue[0]} 1, ${appliedData.current.quarterOption.selectedHeader}`
      ).getMonth();
      const quarter = getQuarter(startMonth);
      return `Q${quarter} ${quarterOption.selectedHeader}`;
    }

    if (appliedData.current.mode === 'month') {
      return `${appliedData.current.monthOption.selectedValue.join(', ')} ${appliedData.current.monthOption.selectedHeader}`;
    }

    if (appliedData.current.mode === 'year') return `${appliedData.current.yearOption.selectedValue}`;
  };
  const [displayText, setDisplayText] = useState(() => handleRenderText());

  useLayoutEffect(() => {
    const isAppy = !checkNotApply();

    if (visible) {
      setMode(appliedData.current.mode);
      setMonthOption(isAppy ? cloneDeep(appliedData.current.monthOption) : monthOptionInitial);
      setQuarterOption(isAppy ? cloneDeep(appliedData.current.quarterOption) : quarterOptionInitial);
      setyearOption(isAppy ? cloneDeep(appliedData.current.yearOption) : yearOptionInitial);
    }
  }, [visible]);

  const handleChangeMode = (mode) => {
    const isNotApply = checkNotApply();

    if (mode === 'month' && isNotApply) {
      setMonthOption({
        ...monthOptionInitial,
      });
    } else if (mode === 'quarter' && isNotApply) {
      setQuarterOption({
        ...quarterOptionInitial,
      });
    } else if (mode === 'year' && isNotApply) {
      setyearOption({
        ...yearOptionInitial,
      });
    } else {
      setMonthOption(cloneDeep(appliedData.current.monthOption));
      setQuarterOption(cloneDeep(appliedData.current.quarterOption));
      setyearOption(cloneDeep(appliedData.current.yearOption));
    }
  };

  const handleYearChange = (direction, type) => {
    const isNotApply = checkNotApply();

    const optionsMap = {
      month: {
        state: monthOption,
        setter: setMonthOption,
        current: currentYear,
        currentValue: [monthNames[currentMonth - 1]],
        applied: appliedData.current.monthOption,
      },
      quarter: {
        state: quarterOption,
        setter: setQuarterOption,
        current: currentYear,
        currentValue: getQuarterMonths(currentQuarter),
        applied: appliedData.current.quarterOption,
      },
      year: {
        state: yearOption,
        setter: setyearOption,
        current: yearViewInitial,
        currentValue: [currentYear],
        applied: appliedData.current.yearOption,
      },
    };

    const { state, setter, current, currentValue, applied } = optionsMap[type];
    const newYear = state.selectedHeader + direction;

    if (newYear === current && isNotApply) {
      setter({
        selectedHeader: newYear,
        selectedValue: currentValue,
      });
    } else if (!isNotApply && applied.selectedValue.length > 0 && newYear === applied.selectedHeader) {
      setter({
        selectedHeader: newYear,
        selectedValue: applied.selectedValue,
      });
    } else {
      setter({
        selectedHeader: newYear,
        selectedValue: [],
      });
    }
  };

  const getQuarter = (startMonth) => Math.floor(startMonth / 3) + 1;

  const setAppliedData = (newData) => {
    appliedData.current = { ...appliedDataInitial, ...newData };
  };

  const handleApplyClick = () => {
    let displayTextValue = '';

    if (mode === 'quarter' && quarterOption.selectedValue.length === 3) {
      const startMonth = new Date(`${quarterOption.selectedValue[0]} 1, ${quarterOption.selectedHeader}`).getMonth();
      const quarter = getQuarter(startMonth);
      displayTextValue = `Q${quarter} ${quarterOption.selectedHeader}`;
      setAppliedData({
        mode,
        quarterOption: cloneDeep(quarterOption),
        monthOption: { ...appliedDataInitial.monthOption, selectedValue: [] },
        yearOption: { ...appliedDataInitial.yearOption, selectedValue: [] },
      });
    } else if (mode === 'month' && monthOption.selectedValue.length > 0) {
      displayTextValue = `${monthOption.selectedValue.join(', ')} ${monthOption.selectedHeader}`;
      setAppliedData({
        mode,
        quarterOption: { ...appliedDataInitial.quarterOption, selectedValue: [] },
        monthOption: cloneDeep(monthOption),
        yearOption: { ...appliedDataInitial.yearOption, selectedValue: [] },
      });
    } else if (mode === 'year' && yearOption.selectedValue.length > 0) {
      displayTextValue = `${yearOption.selectedValue}`;
      setAppliedData({
        mode,
        quarterOption: { ...appliedDataInitial.quarterOption, selectedValue: [] },
        monthOption: { ...appliedDataInitial.monthOption, selectedValue: [] },
        yearOption: cloneDeep(yearOption),
      });
    } else {
      displayTextValue = '';
      setAppliedData(appliedDataInitial);
    }

    setDisplayText(displayTextValue);

    if (onApply) {
      onApply(appliedData.current);
    }

    setVisible(false);
  };

  const handleQuarterClick = (month) => {
    const selectedQuarter = Object.entries(monthsInQuarter).find(([_, months]) => months.includes(month));
    if (!selectedQuarter) return;

    const [quarter] = selectedQuarter;
    const newSelectedValue = monthsInQuarter[quarter];

    const hasExiststQuarter = quarterOption.selectedValue.some((item) => newSelectedValue.includes(item));

    setQuarterOption((pre) => ({
      ...pre,
      selectedValue: hasExiststQuarter ? [] : [...newSelectedValue],
    }));
  };

  const monthMenu = (
    <div className={styles.dropdownPanel}>
      <div className={styles.header}>
        <img src={arrow_prev} title="" alt="" onClick={() => handleYearChange(-1, 'month')} style={{ cursor: 'pointer' }} />
        <span className={styles.yearField}>{monthOption.selectedHeader}</span>
        <img src={arrow_next} title="" alt="" onClick={() => handleYearChange(1, 'month')} style={{ cursor: 'pointer' }} />
      </div>
      <div className={styles.grid}>
        {monthNames.map((month) => (
          <div
            key={month}
            className={`${styles.cell} ${monthOption.selectedValue.includes(month) ? styles.active : ''}`}
            onClick={() => {
              setMonthOption((pre) => ({
                ...pre,
                selectedValue: monthOption.selectedValue[0] === month ? [] : [month],
              }));
            }}
          >
            {month}
          </div>
        ))}
      </div>
      <div className={styles.actions}>
        <BasicButton onClick={() => setVisible(false)} mode="cancel-border" fontSize={14} height={40}>
          Cancel
        </BasicButton>
        <BasicButton
          type="primary"
          onClick={handleApplyClick}
          mode="teal-bold"
          fontSize={14}
          height={40}
          disabled={isDisableApply && monthOption.selectedValue.length === 0}
        >
          Apply
        </BasicButton>
      </div>
    </div>
  );

  const quarterMenu = (
    <div className={styles.dropdownPanel}>
      <div className={styles.header}>
        <img src={arrow_prev} title="" alt="" onClick={() => handleYearChange(-1, 'quarter')} style={{ cursor: 'pointer' }} />
        <span className={styles.yearField}>{quarterOption.selectedHeader}</span>
        <img src={arrow_next} title="" alt="" onClick={() => handleYearChange(1, 'quarter')} style={{ cursor: 'pointer' }} />
      </div>
      <div className={styles.grid}>
        {monthNames.map((month) => (
          <div
            key={month}
            className={`${styles.cellQuarter} ${quarterOption.selectedValue.includes(month) ? styles.active : ''}`}
            onClick={() => handleQuarterClick(month)}
          >
            {month}
          </div>
        ))}
      </div>
      <div className={styles.actions}>
        <BasicButton onClick={() => setVisible(false)} mode="cancel-border" fontSize={14} height={40}>
          Cancel
        </BasicButton>
        <BasicButton
          type="primary"
          onClick={handleApplyClick}
          mode="teal-bold"
          fontSize={14}
          height={40}
          disabled={isDisableApply && quarterOption.selectedValue.length === 0}
        >
          Apply
        </BasicButton>
      </div>
    </div>
  );

  const yearMenu = (
    <div className={styles.dropdownPanel}>
      <div className={styles.header}>
        <img src={arrow_prev} title="" alt="" onClick={() => handleYearChange(-12, 'year')} style={{ cursor: 'pointer' }} />
        <span className={styles.yearField}>
          {yearOption.selectedHeader}-{yearOption.selectedHeader + 11}
        </span>
        <img src={arrow_next} title="" alt="" onClick={() => handleYearChange(12, 'year')} style={{ cursor: 'pointer' }} />
      </div>
      <div className={styles.grid}>
        {Array.from({ length: 12 }, (_, i) => yearOption.selectedHeader + i).map((year) => (
          <div
            key={year}
            className={`${styles.cell} ${yearOption.selectedValue.includes(year) ? styles.active : ''}`}
            onClick={() => {
              setyearOption((pre) => ({
                ...pre,
                selectedValue: yearOption.selectedValue[0] === year ? [] : [year],
              }));
            }}
          >
            {year}
          </div>
        ))}
      </div>
      <div className={styles.actions}>
        <BasicButton onClick={() => setVisible(false)} mode="cancel-border" fontSize={14} height={40}>
          Cancel
        </BasicButton>
        <BasicButton
          type="primary"
          onClick={handleApplyClick}
          mode="teal-bold"
          fontSize={14}
          height={40}
          disabled={isDisableApply && yearOption.selectedValue.length === 0}
        >
          Apply
        </BasicButton>
      </div>
    </div>
  );

  const getMenu = () => {
    switch (mode) {
      case 'month':
        return monthMenu;
      case 'quarter':
        return quarterMenu;
      case 'year':
        return yearMenu;
      default:
        return null;
    }
  };

  const openDropdown = () => {
    setVisible(true);
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={`${styles.datePicker}`}>
        <img src={calendar} alt="dropdown" className={styles.prefix} />
        <BasicSelect
          height={44}
          value={displayText || placeholder}
          onClick={openDropdown}
          className={styles.select}
          open={false}
          suffixIcon={<img src={arrow_calendar} alt="dropdown" />}
        />
      </div>

      {visible && (
        <div className={styles.dropdownWrapper}>
          <Menu
            onClick={(e) => {
              setMode(e.key);
              handleChangeMode(e.key);
            }}
            selectedKeys={[mode]}
            mode="vertical"
            style={{ padding: '0px 10px' }}
          >
            <Menu.Item key="month">By month</Menu.Item>
            <Menu.Item key="quarter">By quarter</Menu.Item>
            <Menu.Item key="year">By year</Menu.Item>
          </Menu>
          {getMenu()}
        </div>
      )}
    </div>
  );
};

export default DatePickerUserManagement;
